import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../api/api";
import { SnackbarContext } from "../App";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Crown,
  Zap,
  Users,
  Shield,
  Clock,
  ExternalLink,
  RefreshCw,
  PlayCircle,
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { trackEvent, AdoptionEvents } from "../lib/analytics";
import { useEntitlements } from "../contexts/EntitlementsContext";
import {
  formatCurrency,
  formatDate,
  formatNumber,
  normalizeApiError,
} from "../lib/i18n";

const STATUS_CONFIG = {
  ACTIVE: {
    labelKey: "billing.status.ACTIVE",
    color:
      "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800",
    icon: CheckCircle2,
    iconColor: "text-green-500",
  },
  TRIALING: {
    labelKey: "billing.status.TRIALING",
    color:
      "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-green-800",
    icon: Clock,
    iconColor: "text-blue-500",
  },
  PAST_DUE: {
    labelKey: "billing.status.PAST_DUE",
    color:
      "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800",
    icon: AlertTriangle,
    iconColor: "text-red-500",
  },
  CANCELED: {
    labelKey: "billing.status.CANCELED",
    color:
      "bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700",
    icon: XCircle,
    iconColor: "text-gray-400",
  },
  EXPIRED: {
    labelKey: "billing.status.EXPIRED",
    color:
      "bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-500 dark:border-gray-700",
    icon: XCircle,
    iconColor: "text-gray-400",
  },
};

const PLAN_ICONS = { FREE: Zap, PRO: Zap, TEAM: Users, ENTERPRISE: Crown };
const PLAN_GRADIENTS = {
  FREE: "from-gray-50 to-gray-100 border-gray-200 dark:from-gray-900 dark:to-gray-950 dark:border-border",
  PRO: "from-blue-50 to-indigo-50 border-blue-200 dark:from-blue-900/20 dark:to-indigo-900/20 dark:border-blue-900/30",
  TEAM: "from-purple-50 to-pink-50 border-purple-200 dark:from-purple-900/20 dark:to-pink-900/20 dark:border-purple-900/30",
  ENTERPRISE:
    "from-amber-50 to-orange-50 border-amber-200 dark:from-amber-900/20 dark:to-orange-900/20 dark:border-amber-900/30",
};
const PLAN_ACCENT = {
  FREE: "text-gray-400 dark:text-gray-500",
  PRO: "text-blue-500",
  TEAM: "text-purple-500",
  ENTERPRISE: "text-amber-500",
};

const PLAN_FEATURE_KEYS = [
  "workflows",
  "sequences",
  "opportunities",
  "tasking",
  "advancedReporting",
  "advancedGovernance",
  "salesDashboard",
  "playbooks",
  "bulkOperations",
  "webhooks",
  "apiAccess",
  "exportGovernance",
];
const PLAN_LIMIT_KEYS = [
  "maxSeats",
  "maxContacts",
  "maxExportsPerMonth",
  "maxOpportunities",
  "maxWorkflows",
  "maxSequences",
  "maxTasksPerMonth",
];

const LIVE_FEATURE_ROWS = [
  { key: "canCreateWorkspace", labelKey: "billing.liveFeatures.canCreateWorkspace" },
  { key: "canInviteMembers", labelKey: "billing.liveFeatures.canInviteMembers" },
  { key: "canUseWorkflows", labelKey: "billing.liveFeatures.canUseWorkflows" },
  { key: "canUseSequences", labelKey: "billing.liveFeatures.canUseSequences" },
  { key: "canUseOpportunities", labelKey: "billing.liveFeatures.canUseOpportunities" },
  { key: "canUseTasking", labelKey: "billing.liveFeatures.canUseTasking" },
  { key: "canUseAdvancedReporting", labelKey: "billing.liveFeatures.canUseAdvancedReporting" },
  { key: "canUseAdvancedGovernance", labelKey: "billing.liveFeatures.canUseAdvancedGovernance" },
  { key: "canUseSalesDashboard", labelKey: "billing.liveFeatures.canUseSalesDashboard" },
  { key: "canUsePlaybooks", labelKey: "billing.liveFeatures.canUsePlaybooks" },
  { key: "canUseBulkOperations", labelKey: "billing.liveFeatures.canUseBulkOperations" },
  { key: "canUseWebhooks", labelKey: "billing.liveFeatures.canUseWebhooks" },
  { key: "canUseApiAccess", labelKey: "billing.liveFeatures.canUseApiAccess" },
  { key: "canUseExportGovernance", labelKey: "billing.liveFeatures.canUseExportGovernance" },
  { key: "canUseSavedViews", labelKey: "billing.liveFeatures.canUseSavedViews" },
];

const LIVE_HARD_LIMIT_ROWS = [
  { key: "maxWorkspaces", labelKey: "billing.hardLimits.maxWorkspaces" },
  { key: "maxSeats", labelKey: "billing.hardLimits.maxSeats" },
  { key: "maxContacts", labelKey: "billing.hardLimits.maxContacts" },
  { key: "maxOpportunities", labelKey: "billing.hardLimits.maxOpportunities" },
  { key: "maxWorkflows", labelKey: "billing.hardLimits.maxWorkflows" },
  { key: "maxSequences", labelKey: "billing.hardLimits.maxSequences" },
  { key: "maxSavedViews", labelKey: "billing.hardLimits.maxSavedViews" },
];

const LIVE_QUOTA_ROWS = [
  { key: "monthlyTasks", labelKey: "billing.quotaLabels.monthlyTasks" },
  { key: "monthlyCrmExports", labelKey: "billing.quotaLabels.monthlyCrmExports" },
  { key: "monthlySearchQueries", labelKey: "billing.quotaLabels.monthlySearchQueries" },
  { key: "monthlyBulkOperations", labelKey: "billing.quotaLabels.monthlyBulkOperations" },
];

const LEGACY_FEATURE_FALLBACK = {
  canUseWorkflows: "workflows",
  canUseSequences: "sequences",
  canUseOpportunities: "opportunities",
  canUseTasking: "tasking",
  canUseAdvancedReporting: "advancedReporting",
  canUseAdvancedGovernance: "advancedGovernance",
  canUseSalesDashboard: "salesDashboard",
  canUsePlaybooks: "playbooks",
  canUseBulkOperations: "bulkOperations",
  canUseWebhooks: "webhooks",
  canUseApiAccess: "apiAccess",
  canUseExportGovernance: "exportGovernance",
};

const toLegacyCommercialStateFromSnapshot = (snapshot) => {
  if (!snapshot) return null;

  const bucket = (domain, key) => snapshot?.[domain]?.[key] || null;
  const asNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const emailCredits = bucket("credits", "emailCredits");
  const exportCredits = bucket("credits", "discoveryExportCredits");
  const adoptionCredits = bucket("credits", "manualAdoptionCredits");
  const searchQuota = bucket("quotas", "monthlySearchQueries");
  const bulkQuota = bucket("quotas", "monthlyBulkOperations");

  const mapped = {
    emailUnlocksCount: emailCredits,
    exportsCount: exportCredits,
    adoptionsCount: adoptionCredits,
    searchQueriesCount: searchQuota,
    bulkOperationsCount: bulkQuota,
  };

  const baseQuota = {};
  const topUps = {};
  const usage = {};
  Object.entries(mapped).forEach(([legacyKey, value]) => {
    baseQuota[legacyKey] = value?.isUnlimited
      ? Infinity
      : asNumber(value?.limit);
    topUps[legacyKey] = value?.isUnlimited ? 0 : asNumber(value?.topUp);
    usage[legacyKey] = asNumber(value?.used);
  });

  return {
    planCode: snapshot?.plan?.code || "FREE",
    status: snapshot?.plan?.status || "ACTIVE",
    baseQuota,
    topUps,
    usage,
  };
};

export default function PlanBilling() {
  const showSnackbar = useContext(SnackbarContext);
  const { t, i18n } = useTranslation();
  const { snapshot, refreshEntitlements } = useEntitlements();
  const [currentSub, setCurrentSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [commercialState, setCommercialState] = useState(null);
  const [billingAudit, setBillingAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const normalizedCommercialState = useMemo(
    () => toLegacyCommercialStateFromSnapshot(snapshot),
    [snapshot],
  );
  const effectiveCommercialState = normalizedCommercialState || commercialState;
  const focusSection = searchParams.get("section") || null;
  const translateFeatureLabel = (key) =>
    t(`billing.planFeatures.${key}`, { defaultValue: key });
  const translateLimitLabel = (key) =>
    t(`billing.limitLabels.${key}`, { defaultValue: key });
  const translatePlanInterval = (interval) =>
    t(`billing.interval.${interval}`, { defaultValue: interval });

  const fmtLimit = (v) => {
    if (v === null || v === undefined) return "0";
    return v === Infinity || v > 999999
      ? t("common.unlimited")
      : formatNumber(v, {}, i18n.language);
  };

  const fmtUsageBucketValue = (value) => {
    if (value === null || value === undefined) return "0";
    if (value === Infinity) return t("common.unlimited");
    const parsed = Number(value);
    return Number.isFinite(parsed)
      ? formatNumber(parsed, {}, i18n.language)
      : "0";
  };

  const isFeatureEnabled = (featureKey) => {
    if (
      snapshot?.featureAccess &&
      Object.prototype.hasOwnProperty.call(snapshot.featureAccess, featureKey)
    ) {
      return snapshot.featureAccess[featureKey] === true;
    }
    const legacyFeatureKey = LEGACY_FEATURE_FALLBACK[featureKey];
    return legacyFeatureKey
      ? !!currentSub?.features?.[legacyFeatureKey]
      : false;
  };

  const loadData = useCallback(async () => {
    try {
      const [subRes, plansRes, commRes, auditRes] = await Promise.all([
        api.get("/api/subscription"),
        api.get("/api/subscription/plans"),
        api.get("/api/billing/state"),
        api.get("/api/billing/audit").catch(() => ({ data: { data: [] } })),
      ]);
      setCurrentSub(subRes.data);
      setPlans(plansRes.data.plans);
      setCommercialState(commRes.data.data);
      setBillingAudit(auditRes.data.data);
      await refreshEntitlements({ silent: true });
    } catch (err) {
      showSnackbar(
        normalizeApiError(err, t, "billing.messages.loadFailed"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [showSnackbar, refreshEntitlements, t]);

  useEffect(() => {
    let mounted = true;

    const performLoad = async () => {
      await loadData();
      if (!mounted) return;

      // Track Billing View
      trackEvent(AdoptionEvents.BILLING_VIEW);

      // Handle return from Checkout
      const checkoutStatus = searchParams.get("checkout");
      if (checkoutStatus === "success") {
        showSnackbar(t("billing.messages.paymentSuccess"), "success");
        // Clean up URL
        setSearchParams({});
      } else if (checkoutStatus === "canceled") {
        showSnackbar(t("billing.messages.checkoutCanceled"), "default");
        setSearchParams({});
      }
    };

    performLoad();
    return () => { mounted = false; };
  }, [loadData, searchParams, setSearchParams, showSnackbar, t]);

  useEffect(() => {
    if (!focusSection || loading) return;
    const target = document.getElementById(focusSection);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [focusSection, loading]);

  const doAction = async (method, path, body = {}) => {
    setActionLoading(true);
    try {
      const res = await api[method](`/api/subscription${path}`, body);
      setCurrentSub(res.data);
      showSnackbar(t("billing.messages.subscriptionUpdated"));
      loadData(); // Reload safely to get new usage bars
    } catch (err) {
      showSnackbar(
        normalizeApiError(err, t, "billing.messages.actionFailed"),
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckout = async (planCode) => {
    setActionLoading(true);
    try {
      const res = await api.post("/api/billing/checkout", { planCode });

      // Track Upgrade Intent
      trackEvent(AdoptionEvents.UPGRADE_INTENT, { planCode });

      // Redirect to Stripe (Simulated) Checkout page
      window.location.href = res.data.url;
    } catch (err) {
      showSnackbar(
        normalizeApiError(err, t, "billing.messages.checkoutFailed"),
        "error",
      );
      setActionLoading(false);
    }
  };

  const handlePortal = async () => {
    setActionLoading(true);
    try {
      const res = await api.post("/api/billing/portal", {});
      // Redirect to Stripe (Simulated) Portal page
      window.location.href = res.data.url;
    } catch (err) {
      showSnackbar(
        normalizeApiError(err, t, "billing.messages.portalFailed"),
        "error",
      );
      setActionLoading(false);
    }
  };

  const handleTopUpRequest = async (type, amount) => {
    setActionLoading(true);
    try {
      await api.post("/api/billing/top-up/request", { type, amount });
      showSnackbar(
        t("billing.messages.topUpSuccess", {
          amount: formatNumber(amount, {}, i18n.language),
          type,
        }),
        "success",
      );
      loadData();
    } catch (err) {
      showSnackbar(
        normalizeApiError(err, t, "billing.messages.actionFailed"),
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        {t("common.loading")}
      </div>
    );

  const sub = currentSub?.subscription;
  const effectiveCode =
    snapshot?.plan?.code || sub?.effectivePlanCode || "FREE";
  const statusCfg = STATUS_CONFIG[sub?.status] || STATUS_CONFIG.ACTIVE;
  const StatusIcon = statusCfg.icon;
  const PlanIcon = PLAN_ICONS[effectiveCode] || Zap;

  return (
    <div className="space-y-8 max-w-6xl mx-auto p-4 lg:p-0">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {t("billing.pageTitle")}
        </h1>
        <p className="text-muted-foreground">
          {t("billing.pageSubtitle")}
        </p>
      </div>

      {/* ─── Current Plan Card ─── */}
      {sub && (
        <div
          id="billing"
          className={`rounded-xl border-2 bg-gradient-to-br p-6 relative overflow-hidden ${PLAN_GRADIENTS[effectiveCode]} ${focusSection === "billing" ? "ring-2 ring-primary/25" : ""}`}
        >
          <div className="flex items-start justify-between flex-wrap gap-4 relative z-10">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-background shadow-sm border border-border flex items-center justify-center">
                <PlanIcon className={`w-6 h-6 ${PLAN_ACCENT[effectiveCode]}`} />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-bold">
                    {currentSub.plan.name} Plan
                  </h2>
                  <span
                    className={`text-xs font-bold px-2 py-0.5 rounded-full border flex items-center gap-1 ${statusCfg.color}`}
                  >
                    <StatusIcon className={`w-3 h-3 ${statusCfg.iconColor}`} />
                    {t(statusCfg.labelKey)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {currentSub.plan.price
                    ? `${formatCurrency(currentSub.plan.price, "USD", {}, i18n.language)}/${translatePlanInterval(currentSub.plan.billingInterval)}`
                    : effectiveCode === "ENTERPRISE"
                      ? t("billing.plan.customPricing")
                      : t("billing.plan.freeForever")}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {sub.billingCustomerId && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={actionLoading}
                  onClick={handlePortal}
                >
                  <ExternalLink className="w-3 h-3 mr-1" />{" "}
                  {t("billing.buttons.manageCommerceProfile")}
                </Button>
              )}
              {sub.cancelAtPeriodEnd &&
                !["CANCELED", "EXPIRED"].includes(sub.status) && (
                  <Button
                    size="sm"
                    variant="outline"
                  disabled={actionLoading}
                  onClick={() => doAction("put", "/reactivate")}
                >
                  <RefreshCw className="w-3 h-3 mr-1" />{" "}
                  {t("billing.buttons.undoCancellation")}
                </Button>
              )}
              {["CANCELED", "EXPIRED"].includes(sub.status) &&
                sub.planCode !== "FREE" && (
                  <Button
                    size="sm"
                  disabled={actionLoading}
                  onClick={() => doAction("put", "/reactivate")}
                >
                  <RefreshCw className="w-3 h-3 mr-1" />{" "}
                  {t("billing.buttons.reactivate")}
                </Button>
              )}
              {!sub.billingCustomerId &&
                !sub.cancelAtPeriodEnd &&
                ["ACTIVE", "TRIALING"].includes(sub.status) &&
                sub.planCode !== "FREE" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    disabled={actionLoading}
                    onClick={() => doAction("put", "/cancel")}
                  >
                    {t("billing.buttons.cancelPlan")}
                  </Button>
                )}
            </div>
          </div>

          {sub.downgraded && (
            <div className="mt-4 flex items-center gap-2 text-rose-700 bg-rose-100 px-3 py-2 rounded-lg text-sm font-medium border border-rose-200">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <strong>{t("billing.alerts.accessRestrictedTitle")}:</strong>{" "}
              {t("billing.alerts.accessRestrictedDescription")}
            </div>
          )}
          {sub.status === "TRIALING" && sub.trialEndsAt && (
            <div className="mt-4 flex items-center gap-2 text-indigo-700 bg-indigo-100 px-3 py-2 rounded-lg text-sm font-medium border border-indigo-200">
              <Clock className="w-4 h-4 flex-shrink-0" />
              <strong>{t("billing.alerts.fullFeatureTrialTitle")}:</strong>{" "}
              {t("billing.alerts.fullFeatureTrialDescription", {
                date: formatDate(sub.trialEndsAt, { dateStyle: "medium" }, i18n.language),
              })}
            </div>
          )}
          {sub.status === "PAST_DUE" && (
            <div className="mt-4 flex items-center gap-2 text-rose-700 bg-rose-100 px-3 py-2 rounded-lg text-sm font-medium border border-rose-200">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <strong>{t("billing.alerts.paymentRequiredTitle")}:</strong>{" "}
              {t("billing.alerts.paymentRequiredDescription")}
            </div>
          )}
          {sub.cancelAtPeriodEnd &&
            !["CANCELED", "EXPIRED"].includes(sub.status) && (
              <div className="mt-4 flex items-center gap-2 text-orange-700 bg-orange-100 px-3 py-2 rounded-lg text-sm font-medium">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />{" "}
                {t("billing.alerts.cancellationNotice")}
              </div>
            )}

          {sub.currentPeriodStart && sub.currentPeriodEnd && (
            <p className="mt-3 text-xs text-muted-foreground relative z-10">
              {t("billing.sections.currentPeriod")}:{" "}
              {formatDate(sub.currentPeriodStart, { dateStyle: "medium" }, i18n.language)}{" "}
              —{" "}
              {formatDate(sub.currentPeriodEnd, { dateStyle: "medium" }, i18n.language)}
            </p>
          )}
        </div>
      )}

      {/* ─── Current Entitlements & Discovery Usage ─── */}
      {currentSub && effectiveCommercialState && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Discovery Credits (Global Pool) */}
          <div
            id="credits"
            className={`bg-card rounded-xl shadow border border-border p-6 flex flex-col ${focusSection === "credits" || focusSection === "usage" ? "ring-2 ring-primary/25" : ""}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-5 h-5 text-indigo-500" />
              <h3 className="text-lg font-semibold text-foreground">
                {t("billing.sections.discoveryCredits")}
              </h3>
              <Badge
                variant="outline"
                className="ml-auto text-[9px] font-black uppercase tracking-tighter"
              >
                {t("billing.plan.usageBased")}
              </Badge>
            </div>
            <p className="text-[11px] text-muted-foreground mb-2 leading-relaxed">
              {t("billing.discoveryCreditsDescription")}
            </p>
            <p className="text-[11px] text-indigo-600 dark:text-indigo-400 mb-6 font-medium leading-relaxed">
              {t("billing.unlockingHint")}
            </p>
            {normalizedCommercialState && (
              <p className="text-[10px] text-muted-foreground mb-6">
                {t("billing.runtimeBalances")}
              </p>
            )}

            <div className="space-y-6 flex-grow">
              {[
                {
                  key: "emailUnlocksCount",
                  label: t("billing.metrics.emailUnlocks.label"),
                  detail: t("billing.metrics.emailUnlocks.detail"),
                  topUpAmount: 500,
                  alwaysVisible: true,
                },
                {
                  key: "adoptionsCount",
                  label: t("billing.metrics.manualContactAdoptions.label"),
                  detail: t("billing.metrics.manualContactAdoptions.detail"),
                  topUpAmount: 500,
                  alwaysVisible: false,
                },
                {
                  key: "exportsCount",
                  label: t("billing.metrics.csvExports.label"),
                  detail: t("billing.metrics.csvExports.detail"),
                  topUpAmount: 1000,
                  alwaysVisible: true,
                },
                {
                  key: "searchQueriesCount",
                  label: t("billing.metrics.premiumDiscoverySearches.label"),
                  detail: t("billing.metrics.premiumDiscoverySearches.detail"),
                  topUpAmount: 1000,
                  alwaysVisible: true,
                },
              ]
                .filter((metric) => {
                  if (metric.alwaysVisible) return true;
                  const used = Number(
                    effectiveCommercialState.usage?.[metric.key] || 0,
                  );
                  const topUp = Number(
                    effectiveCommercialState.topUps?.[metric.key] || 0,
                  );
                  return used > 0 || topUp > 0;
                })
                .map((metric) => {
                  const base = Number(
                    effectiveCommercialState.baseQuota?.[metric.key] || 0,
                  );
                  const topUp = Number(
                    effectiveCommercialState.topUps?.[metric.key] || 0,
                  );
                  const used = Number(
                    effectiveCommercialState.usage?.[metric.key] || 0,
                  );
                  const total = base + topUp;
                  const pct =
                    Number.isFinite(total) && total > 0
                      ? Math.min(100, (used / total) * 100)
                      : 0;

                  return (
                    <div key={metric.key} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-black uppercase tracking-widest text-[10px] text-slate-500">
                          {metric.label}
                        </span>
                        <span className="text-xs font-bold text-muted-foreground">
                          <span className="text-foreground font-bold">
                            {fmtUsageBucketValue(used)}
                          </span>{" "}
                          / {fmtUsageBucketValue(total)}
                        </span>
                      </div>
                      <div className="text-[10px] text-muted-foreground font-medium">
                        {metric.detail}
                      </div>
                      <div className="h-2.5 w-full bg-muted rounded-full overflow-hidden flex">
                        <div
                          className={`h-full transition-all duration-700 ${pct > 90 ? "bg-rose-500" : pct > 70 ? "bg-amber-500" : "bg-indigo-500"}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <div className="flex gap-2">
                          {topUp > 0 && (
                            <span className="text-[10px] bg-indigo-500/10 text-indigo-500 px-1.5 py-0.5 rounded font-bold">
                              {t("billing.topUpBadge", {
                                count: formatNumber(topUp, {}, i18n.language),
                              })}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() =>
                            handleTopUpRequest(metric.key, metric.topUpAmount)
                          }
                          disabled={actionLoading}
                          className="text-[10px] font-bold text-indigo-500 hover:text-indigo-600 uppercase tracking-wider transition-colors"
                        >
                          {t("billing.buttons.addCredits")}
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>

            {billingAudit.length > 0 && (
              <div className="mt-8 pt-6 border-t border-border/50">
                <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-4">
                  {t("billing.sections.recentBillingHistory")}
                </h4>
                <div className="space-y-3">
                  {billingAudit.slice(0, 3).map((log) => (
                    <div
                      key={log._id}
                      className="flex items-center justify-between text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <Clock className="w-3 h-3 text-muted-foreground" />
                        <span className="text-muted-foreground">
                          {formatDate(log.createdAt, { dateStyle: "medium" }, i18n.language)}
                        </span>
                        <span className="font-medium text-foreground">
                          {log.action.replace(/_/g, " ")}
                        </span>
                      </div>
                      <span className="text-muted-foreground font-medium">
                        {log.details?.amount ||
                          log.details?.plan ||
                          log.details?.to ||
                          ""}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Platform Entitlements (General SaaS) */}
          <div
            id="features"
            className={`bg-card rounded-xl shadow border border-border p-6 flex flex-col ${focusSection === "features" || focusSection === "billing" ? "ring-2 ring-primary/25" : ""}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-5 h-5 text-slate-500" />
              <h3 className="text-lg font-semibold text-foreground">
                {t("billing.sections.platformEntitlements")}
              </h3>
              <Badge
                variant="outline"
                className="ml-auto text-[9px] font-black uppercase tracking-tighter"
              >
                {t("billing.plan.tierBased")}
              </Badge>
            </div>
            <p className="text-[11px] text-muted-foreground mb-6 leading-relaxed">
              {t("billing.platformDescription")}
            </p>

            <div className="space-y-5 flex-grow">
              <div>
                <h4 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground mb-2">
                  {t("billing.sections.includedFeatures")}
                </h4>
                <div className="space-y-1.5">
                  {LIVE_FEATURE_ROWS.map(({ key, labelKey }) => (
                    <div
                      key={key}
                      className="flex items-center justify-between py-1.5 border-b border-border/30 last:border-0 hover:bg-slate-500/5 transition-colors px-1 rounded"
                    >
                      <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                        {t(labelKey)}
                      </span>
                      {isFeatureEnabled(key) ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-slate-300 dark:text-slate-800" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {snapshot && (
                <>
                  <div>
                    <h4 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground mb-2">
                      {t("billing.sections.hardLimits")}
                    </h4>
                    <div className="space-y-1.5">
                      {LIVE_HARD_LIMIT_ROWS.map(({ key, labelKey }) => {
                        const bucket = snapshot?.hardLimits?.[key];
                        if (!bucket) return null;
                        return (
                          <div
                            key={key}
                            className="flex items-center justify-between py-1.5 border-b border-border/30 last:border-0 px-1 rounded"
                          >
                            <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                              {t(labelKey)}
                            </span>
                            <span className="text-xs font-bold text-muted-foreground">
                              <span className="text-foreground">
                                {fmtUsageBucketValue(bucket.used)}
                              </span>{" "}
                              / {fmtUsageBucketValue(bucket.limit)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div id="usage">
                    <h4 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground mb-2">
                      {t("billing.sections.cycleQuotas")}
                    </h4>
                    <div className="space-y-1.5">
                      {LIVE_QUOTA_ROWS.map(({ key, labelKey }) => {
                        const bucket = snapshot?.quotas?.[key];
                        if (!bucket) return null;
                        return (
                          <div
                            key={key}
                            className="flex items-center justify-between py-1.5 border-b border-border/30 last:border-0 px-1 rounded"
                          >
                            <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                              {t(labelKey)}
                            </span>
                            <span className="text-xs font-bold text-muted-foreground">
                              <span className="text-foreground">
                                {fmtUsageBucketValue(bucket.used)}
                              </span>{" "}
                              / {fmtUsageBucketValue(bucket.total)}
                              <span className="ml-2 text-[10px] text-muted-foreground">
                                {t("billing.sections.remaining")}{" "}
                                {fmtUsageBucketValue(bucket.remaining)}
                              </span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {!snapshot && (
                <div className="rounded-lg border border-dashed border-border/70 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                  {t("billing.snapshotFallback")}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Plan Comparison ─── */}
      <div className="bg-card rounded-xl shadow border border-border p-6">
        <h3 className="text-lg font-semibold mb-6">
          {t("billing.sections.comparePlans")}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {plans.map((p) => {
            const Icon = PLAN_ICONS[p.code] || Zap;
            const isCurrent = effectiveCode === p.code;
            const isUpgrade =
              plans.findIndex((x) => x.code === p.code) >
              plans.findIndex((x) => x.code === effectiveCode);
            const isDowngrade =
              plans.findIndex((x) => x.code === p.code) <
              plans.findIndex((x) => x.code === effectiveCode);
            return (
              <div
                key={p.code}
                className={`rounded-xl border-2 p-5 transition-all ${isCurrent ? "border-primary shadow-md bg-primary/5 ring-2 ring-primary/20" : "border-gray-200 hover:border-gray-300"}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-5 h-5 ${PLAN_ACCENT[p.code]}`} />
                  <span className="font-bold">{p.name}</span>
                </div>
                <p className="text-2xl font-bold mb-1">
                  {p.price
                    ? formatCurrency(p.price, "USD", {}, i18n.language)
                    : p.code === "ENTERPRISE"
                      ? t("billing.plan.custom")
                      : t("billing.plan.free")}
                  {p.billingInterval && p.billingInterval !== "custom" && (
                    <span className="text-sm font-normal text-muted-foreground">
                      /{translatePlanInterval(p.billingInterval)}
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  {p.description}
                </p>

                <div className="space-y-1 mb-4">
                  {PLAN_FEATURE_KEYS
                    .filter((key) => p.features[key])
                    .slice(0, 5)
                    .map((key) => (
                      <div
                        key={key}
                        className="flex items-center gap-1.5 text-xs text-muted-foreground"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />{" "}
                        {translateFeatureLabel(key)}
                      </div>
                    ))}
                  <p className="text-xs text-muted-foreground/60">
                    {fmtLimit(p.limits.maxContacts)}{" "}
                    {translateLimitLabel("maxContacts").toLowerCase()} ·{" "}
                    {fmtLimit(p.limits.maxSeats)}{" "}
                    {translateLimitLabel("maxSeats").toLowerCase()}
                  </p>
                </div>

                {isCurrent ? (
                  <div className="text-center text-sm font-semibold text-primary py-2">
                    {t("billing.plan.currentPlan")}
                  </div>
                ) : p.code === "ENTERPRISE" ? (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() =>
                      window.open(
                        "mailto:sales@mailwise.app?subject=Enterprise Plan Inquiry",
                        "_blank",
                      )
                    }
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />{" "}
                    {t("billing.buttons.contactSales")}
                  </Button>
                ) : isUpgrade || isDowngrade ? (
                  <Button
                    className="w-full"
                    disabled={actionLoading}
                    variant={isDowngrade ? "outline" : "default"}
                    onClick={() => handleCheckout(p.code)}
                  >
                    {isUpgrade
                      ? t("billing.buttons.continueCheckout")
                      : t("billing.switchTo", { plan: p.name })}
                  </Button>
                ) : null}
              </div>
            );
          })}
        </div>

        {/* Trial CTA */}
        {effectiveCode === "FREE" && sub?.status !== "TRIALING" && (
          <div className="border-t border-border pt-6 text-center">
            <p className="text-sm text-muted-foreground mb-3">
              {t("billing.sections.trialCta")}
            </p>
            <div className="flex justify-center gap-3">
              <Button
                variant="outline"
                disabled={actionLoading}
                onClick={() =>
                  doAction("post", "/start-trial", {
                    planCode: "PRO",
                    trialDays: 14,
                  })
                }
              >
                <PlayCircle className="w-4 h-4 mr-1" />{" "}
                {t("billing.buttons.tryProFree")}
              </Button>
              <Button
                variant="outline"
                disabled={actionLoading}
                onClick={() =>
                  doAction("post", "/start-trial", {
                    planCode: "TEAM",
                    trialDays: 14,
                  })
                }
              >
                <PlayCircle className="w-4 h-4 mr-1" />{" "}
                {t("billing.buttons.tryTeamFree")}
              </Button>
            </div>
          </div>
        )}

        {/* Detailed feature comparison table */}
        <div className="border-t border-border pt-6 mt-6">
          <h4 className="font-semibold text-sm mb-4 text-muted-foreground">
            {t("billing.sections.detailedFeatureComparison")}
          </h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-2 text-muted-foreground w-48 font-medium">
                    {t("billing.sections.featureLimit")}
                  </th>
                  {plans.map((p) => (
                    <th
                      key={p.code}
                      className={`text-center py-2 px-3 ${effectiveCode === p.code ? "bg-primary/5 dark:bg-primary/10 font-bold" : "font-medium"}`}
                    >
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PLAN_FEATURE_KEYS.map((key) => (
                  <tr key={key} className="border-b border-border/50">
                    <td className="py-1.5 px-2 text-muted-foreground text-xs">
                      {translateFeatureLabel(key)}
                    </td>
                    {plans.map((p) => (
                      <td
                        key={p.code}
                        className={`py-1.5 px-3 text-center ${effectiveCode === p.code ? "bg-primary/5 dark:bg-primary/10" : ""}`}
                      >
                        {p.features[key] ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mx-auto" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-muted-foreground/20 mx-auto" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td
                    colSpan={plans.length + 1}
                    className="py-1 border-t border-border"
                  ></td>
                </tr>
                {PLAN_LIMIT_KEYS.map((key) => (
                  <tr key={key} className="border-b border-border/50">
                    <td className="py-1.5 px-2 text-muted-foreground text-xs">
                      {translateLimitLabel(key)}
                    </td>
                    {plans.map((p) => (
                      <td
                        key={p.code}
                        className={`py-1.5 px-3 text-center text-xs font-semibold text-foreground ${effectiveCode === p.code ? "bg-primary/5 dark:bg-primary/10" : ""}`}
                      >
                        {fmtLimit(p.limits[key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
